import type * as React from "react";

import { cn } from "../lib/utils";
import { Label } from "./label";

/**
 * Layout for one form control with its label, help text and error. The behaviour (which control,
 * which error) is the form library's; these only arrange and style. `data-invalid` on `Field`
 * colours the label and error.
 */

function FieldGroup({ className, ...props }: React.ComponentProps<"div">) {
	return (
		<div
			data-slot="field-group"
			className={cn("group/field-group flex w-full flex-col gap-5", className)}
			{...props}
		/>
	);
}

function Field({ className, ...props }: React.ComponentProps<"div">) {
	return (
		<div
			role="group"
			data-slot="field"
			className={cn("group/field flex w-full flex-col gap-2 data-[invalid=true]:text-destructive", className)}
			{...props}
		/>
	);
}

function FieldLabel({ className, ...props }: React.ComponentProps<typeof Label>) {
	return (
		<Label
			data-slot="field-label"
			className={cn("w-fit leading-snug group-data-[disabled=true]/field:opacity-50", className)}
			{...props}
		/>
	);
}

function FieldDescription({ className, ...props }: React.ComponentProps<"p">) {
	return (
		<p
			data-slot="field-description"
			className={cn("text-sm leading-normal font-normal text-muted-foreground", className)}
			{...props}
		/>
	);
}

function FieldError({ className, children, ...props }: React.ComponentProps<"div">) {
	if (!children) return null;
	return (
		<div
			role="alert"
			data-slot="field-error"
			className={cn("text-sm font-normal text-destructive", className)}
			{...props}
		>
			{children}
		</div>
	);
}

export { Field, FieldDescription, FieldError, FieldGroup, FieldLabel };
